import { API_HOST, STORAGE_KEY } from "@/lib/config"
import type { PuzzleTopology } from "../types/puzzle"

const TRPC_BASE = `${API_HOST}/trpc`
const API_BASE = `${API_HOST}/api/v1/qb`

function getAuthHeaders(): HeadersInit {
  const headers: HeadersInit = {}
  const token = localStorage.getItem(STORAGE_KEY)
  if (token) {
    ;(headers as Record<string, string>).Authorization = `Bearer ${token}`
  }
  return headers
}

async function tRPCRequest<T>(
  procedure: string,
  input?: unknown,
): Promise<T> {
  const token = localStorage.getItem(STORAGE_KEY)
  const headers: HeadersInit = {
    "Content-Type": "application/json",
  }
  if (token) {
    headers["Authorization"] = `Bearer ${token}`
  }

  const response = await fetch(`${TRPC_BASE}/${procedure}`, {
    method: "POST",
    headers,
    body: input !== undefined ? JSON.stringify(input) : undefined,
  })

  const data = await response.json()

  if (!response.ok) {
    const errorMessage = data?.error?.message || "请求失败"
    try {
      const parsed = JSON.parse(errorMessage)
      if (Array.isArray(parsed) && parsed[0]?.message) {
        throw new Error(parsed.map((e: any) => e.message).join(", "))
      }
    } catch (e) {
      // Not JSON, use as-is
    }
    throw new Error(errorMessage)
  }

  return data.result.data as T
}

export function getFileUrl(path: string): string {
  if (!path) return ""
  const trimmed = path.trim()
  if (!trimmed) return ""
  if (trimmed.startsWith("http")) return trimmed

  const cleaned = trimmed.replace(/^\.\/+/, "")
  if (cleaned.startsWith("/static/")) return `${API_HOST}${cleaned}`
  if (cleaned.startsWith("/images/"))
    return `${API_HOST}/media/qb_images${cleaned}`
  // Fix stale paths with extra "images/" directory (e.g. /media/qb_images/images/{hash}.jpg → /media/qb_images/{hash}.jpg)
  const qbImagesMatch = cleaned.match(/^\/media\/qb_images\/images\/([0-9a-f]{64}\.(png|jpg|jpeg|webp|gif))$/i)
  if (qbImagesMatch)
    return `${API_HOST}/media/qb_images/${qbImagesMatch[1]}`
  if (cleaned.startsWith("/")) return `${API_HOST}${cleaned}`
  if (cleaned.startsWith("media/")) return `${API_HOST}/${cleaned}`
  if (cleaned.startsWith("qb_images/"))
    return `${API_HOST}/media/${cleaned}`
  if (cleaned.startsWith("images/"))
    return `${API_HOST}/media/qb_images/${cleaned}`
  if (/^[0-9a-f]{64}\.(png|jpg|jpeg|webp|gif)$/i.test(cleaned)) {
    return `${API_HOST}/media/qb_images/${cleaned}`
  }

  const fileId = cleaned.split(".")[0]
  return `${API_BASE}/files/${fileId}`
}

export interface AutoExplanation {
  /** 讲解形式标识，当前为 'microcourse_deck' */
  format: string
  version: number
  /** MicrocourseProject 完整 JSON */
  project: Record<string, any>
}

export interface QuestionDraft {
  content: {
    stem: string
    images: string[]
  }
  type: string
  options?: { label: string; content: string; images?: string[] }[]
  answer: {
    value: string
    images: string[]
  }
  analysis?: string
  difficulty: number
  knowledge_points: string[]
  tags?: Record<string, any>
  original_text?: string
  id?: string
  subject?: string
  stage?: string
  grade?: string
  source_type?: string
  source_name?: string
  /** AI 自动生成的讲解缓存（qb.get 返回 camelCase） */
  autoExplanation?: AutoExplanation | null
  auto_explanation?: AutoExplanation | null
}

export interface UploadResponse {
  file_id: string
  url: string
  filename: string
}

export async function uploadFile(file: File): Promise<UploadResponse> {
  const formData = new FormData()
  formData.append("file", file)

  const response = await fetch(`${API_BASE}/upload`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: formData,
  })

  if (!response.ok) {
    const errorText = await response.text().catch(() => 'No error details')
    let errorMessage = `Upload failed: ${response.status} ${response.statusText}`
    try {
      const errorJson = JSON.parse(errorText)
      errorMessage = errorJson.error || errorMessage
    } catch {}
    throw new Error(errorMessage)
  }
  return response.json()
}

export interface PuzzleExtractResponse {
  topology: PuzzleTopology
}

export async function extractPuzzle(
  fileId: string,
): Promise<PuzzleExtractResponse> {
  const response = await fetch(`${API_BASE}/extract-puzzle`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...getAuthHeaders(),
    },
    body: JSON.stringify({ file_id: fileId }),
  })

  if (!response.ok) {
    throw new Error("Extract puzzle failed")
  }
  return response.json()
}

export interface GroupDraft {
  type: string
  passage_content?: string
  audio_url?: string
  images?: string[]
  options?: { label: string; content: string }[]
  questions: QuestionDraft[]
  subject?: string
  grade?: string
  stage?: string
}

export interface ExtractionResult {
  questions: QuestionDraft[]
  groups?: GroupDraft[]
  metadata?: {
    subject?: string
    stage?: string
    grade?: string
  }
  paperMetadata?: {
    name?: string | null
    year?: number | null
    region?: string | null
    exam_type?: string | null
  }
}

export async function extractQuestions(
  fileId: string,
): Promise<ExtractionResult> {
  return tRPCRequest<ExtractionResult>("qb.extractFromImages", {
    fileIds: [fileId],
  })
}

export interface MarkdownExtractionResult extends ExtractionResult {
  markdown?: string
}

export async function extractQuestionsMarkdown(
  fileId: string,
): Promise<MarkdownExtractionResult> {
  return tRPCRequest<MarkdownExtractionResult>("qb.extractFromFile", {
    fileIds: [fileId],
  })
}

export async function extractQuestionsMarkdownBatch(
  fileIds: string[],
  answerFileIds: string[] = [],
): Promise<MarkdownExtractionResult> {
  return tRPCRequest<MarkdownExtractionResult>("qb.extractFromFile", {
    fileIds,
    answerFileIds,
  })
}

export interface SaveResponse {
  saved_count: number
  failed_count: number
  errors: { index: number; error: string }[]
}

export async function saveQuestions(
  questions: QuestionDraft[],
  groups?: GroupDraft[],
  paperId?: string,
): Promise<SaveResponse> {
  return tRPCRequest<SaveResponse>("qb.save", { questions, groups, paperId })
}

export async function updateQuestion(
  questionId: string,
  payload: Partial<QuestionDraft> & { knowledgePoints?: string[] },
): Promise<void> {
  return tRPCRequest<void>("qb.update", { id: questionId, ...payload })
}

export async function fetchQuestionDetail(
  questionId: string,
): Promise<QuestionDraft> {
  const token = localStorage.getItem(STORAGE_KEY)
  const headers: HeadersInit = { "Content-Type": "application/json" }
  if (token) headers["Authorization"] = `Bearer ${token}`
  const url = `${TRPC_BASE}/qb.get?input=${encodeURIComponent(JSON.stringify({ id: questionId }))}`
  const response = await fetch(url, { method: "GET", headers })
  const data = await response.json()
  if (!response.ok) {
    throw new Error(data?.error?.message || "请求失败")
  }
  return data.result?.data ?? data
}
